'use client';
import { jsx } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import { forwardRef, useState, useRef, useEffect, createContext } from 'react';
import { useMergeRefs } from '../hooks/useMergeRefs/useMergeRefs.js';

const RovingFocusContext = createContext({
    elements: { current: new Map() },
    getOrderedItems: () => [],
    setFocusableValue: () => {
        /* intentionally empty */
    },
    onShiftTab: () => {
        /* intentionally empty */
    },
    focusableValue: null,
    orientation: 'horizontal',
});
const RovingFocusRoot = forwardRef(({ activeValue, asChild, orientation = 'horizontal', onBlur, onFocus, ...rest }, ref) => {
    const Component = asChild ? Slot : 'div';
    const [focusableValue, setFocusableValue] = useState(null);
    const [isShiftTabbing, setIsShiftTabbing] = useState(false);
    const elements = useRef(new Map());
    const myRef = useRef(null);
    const refs = useMergeRefs([ref, myRef]);
    const getOrderedItems = () => {
        if (!myRef.current)
            return [];
        const elementsFromDOM = Array.from(myRef.current.querySelectorAll('[data-roving-tabindex-item]'));
        return Array.from(elements.current)
            .sort((a, b) => elementsFromDOM.indexOf(a[1]) - elementsFromDOM.indexOf(b[1]))
            .map(([value, element]) => ({ value, element }));
    };
    useEffect(() => {
        setFocusableValue(activeValue ?? null);
    }, [activeValue]);
    return (jsx(RovingFocusContext.Provider, { value: {
            elements,
            getOrderedItems,
            focusableValue,
            setFocusableValue,
            onShiftTab: () => {
                setIsShiftTabbing(true);
            },
            orientation,
        }, children: jsx(Component, { ...rest, tabIndex: isShiftTabbing ? -1 : 0, onBlur: (e) => {
                onBlur?.(e);
                setIsShiftTabbing(false);
                setFocusableValue(activeValue ?? null);
            }, onFocus: (e) => {
                onFocus?.(e);
                if (e.target !== e.currentTarget)
                    return;
                const orderedItems = getOrderedItems();
                if (orderedItems.length === 0)
                    return;
                if (focusableValue != null) {
                    elements.current.get(focusableValue)?.focus();
                }
                else if (activeValue != null) {
                    elements.current.get(activeValue)?.focus();
                }
                else {
                    orderedItems.at(0)?.element.focus();
                }
            }, ref: refs }) }));
});

export { RovingFocusContext, RovingFocusRoot };
